import {useEffect, useState} from 'react';
import useWebinarApi from '@/Hooks/WebinarApi/useWebinarApi';
import {getLocalStorage} from '@/Utils/CommonUtils';
import {
  Persona,
  Webinar,
  classifyWebinars,
  pickPersona,
} from './personaHelpers';

// Force-resolves so the dashboard never blocks on a slow/failed webinar
// fetch — the persona just falls back to self_learner.
const RESOLVE_TIMEOUT_MS = 3000;

/**
 * Resolves the dashboard persona purely from the signed-in user's real data:
 *
 *   player_type === 1                     -> first_timer
 *   has a class today (webinar_date)      -> csoc_class_day
 *   has an upcoming class                 -> csoc_rest_day
 *   otherwise                             -> self_learner
 *
 * "CSOC" is never a backend flag — it's inferred from the user actually having
 * webinars on tournament/v1/user_webinar_detail.
 */
export default function usePersona(
  isBeginnerPlayer: boolean,
  // user_key straight off the summary payload (data.user.user_key) — the same
  // source Books/index.tsx feeds the Meetings tab, which is why Meetings shows
  // classes correctly. localStorage is only a fallback: 'userkey' is written at
  // login and 'user_key' only when a coach switches into a student's profile,
  // so neither is reliably present, and reading them alone made this call fail
  // with 400 "User key not provided" for real users who do have classes.
  summaryUserKey?: string,
) {
  const [webinarList, setWebinarList] = useState<Webinar[]>([]);
  // Same response, second list: the classes the student has already had. Only
  // the persona needed `webinar_list` before; the curriculum panel needs both
  // so it can tick off what's done.
  const [pastWebinarList, setPastWebinarList] = useState<Webinar[]>([]);
  const [resolving, setResolving] = useState(!isBeginnerPlayer);

  const {mutate: fetchWebinars} = useWebinarApi((resp: any) => {
    // The endpoint answers with an axios response wrapping
    // { webinar_list, past_webinar_list, ... } — NOT a bare array. Reading the
    // response as an array (as this did originally) always yielded [], so
    // classifyWebinars saw no classes and the persona could only ever resolve
    // to self_learner/first_timer: both CSOC branches were unreachable.
    // Same access path Meetings.tsx uses (`data?.data?.webinar_list`).
    const list =
      resp?.data?.webinar_list ??
      resp?.webinar_list ??
      (Array.isArray(resp?.data) ? resp.data : null) ??
      (Array.isArray(resp) ? resp : []);
    setWebinarList(Array.isArray(list) ? list : []);
    const past = resp?.data?.past_webinar_list ?? resp?.past_webinar_list ?? [];
    setPastWebinarList(Array.isArray(past) ? past : []);
    setResolving(false);
  });

  useEffect(() => {
    if (isBeginnerPlayer) {
      setResolving(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // Summary payload first; storage keys only as a fallback.
      const userKey =
        summaryUserKey ||
        (await getLocalStorage('user_key')) ||
        (await getLocalStorage('userkey'));
      if (cancelled) return;
      if (!userKey) {
        // Summary hasn't landed yet — bail without firing a guaranteed 400.
        // The effect re-runs when summaryUserKey arrives.
        setResolving(false);
        return;
      }
      fetchWebinars({
        user_key: userKey,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    })();
    const timer = setTimeout(() => setResolving(false), RESOLVE_TIMEOUT_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeginnerPlayer, summaryUserKey]);

  const classes = classifyWebinars(webinarList);
  const persona: Persona = pickPersona(!!isBeginnerPlayer, classes);

  return {persona, classes, webinarList, pastWebinarList, resolving};
}
